import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Edit2, Save, X } from 'lucide-react';
import { Client, DRVRequirements } from '@/types/client';
import { DRVGuideline } from '@/types/calculation-formulas';
import { getViableFormulas, updateClient } from '@/lib/api/clients';
import { getCountryCode } from '@/utils/country-code-mapping';
import { LabeledSelect } from '@/components/shared/LabeledSelect';
import { showSuccessToast, showErrorToast } from '@/utils/toast-utils';

interface NutrientGuidelinesProps {
  client: Client;
  refreshClientData?: () => Promise<void>;
}

export const NutrientGuidelines: React.FC<NutrientGuidelinesProps> = ({ client, refreshClientData }) => {
  // State for DRV source editing
  const [isEditingDRVSource, setIsEditingDRVSource] = useState(false);
  const [selectedDRVSource, setSelectedDRVSource] = useState<string>('');
  const [drvGuidelines, setDrvGuidelines] = useState<DRVGuideline[]>([]);
  const [isLoadingDRVGuidelines, setIsLoadingDRVGuidelines] = useState(false);
  const [isSavingDRVSource, setIsSavingDRVSource] = useState(false);

  // Get DRV requirements from client
  const drvRequirements = client.drvRequirements;

  // Calculate age from date of birth
  const age = useMemo(() => {
    if (!client?.dateOfBirth) return null;
    const today = new Date();
    const birthDate = new Date(client.dateOfBirth);
    let calculatedAge = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      calculatedAge--;
    }
    return Math.max(0, calculatedAge);
  }, [client?.dateOfBirth]);

  // Fetch DRV guidelines when client data is loaded
  useEffect(() => {
    const fetchDRVGuidelines = async () => {
      if (!client || !age) return;

      setIsLoadingDRVGuidelines(true);
      try {
        const countryCode = client.location ? getCountryCode(client.location) : undefined;
        const response = await getViableFormulas({
          age: age,
          gender: client.gender,
          countryCode: countryCode,
        });
        setDrvGuidelines(response.availableDRVGuidelines || []);
      } catch (error) {
        console.error('Error fetching DRV guidelines:', error);
        setDrvGuidelines([]);
      } finally {
        setIsLoadingDRVGuidelines(false);
      }
    };

    fetchDRVGuidelines();
  }, [client, age]);

  // Map drvSource to guideline ID when guidelines are loaded
  useEffect(() => {
    if (!client || drvGuidelines.length === 0) return;

    // Try to find the guideline that matches the current drvSource
    const currentSource = client.drvSource || (client as any).guidelineUsed;
    if (currentSource) {
      // First try to find by ID
      let matchingGuideline = drvGuidelines.find(g => g.id === currentSource);
      
      // If not found, try to match by name or displayName (case-insensitive, with underscores/hyphens normalized)
      if (!matchingGuideline) {
        const normalizedSource = currentSource.toLowerCase().replace(/[_-]/g, '');
        matchingGuideline = drvGuidelines.find(g => {
          const normalizedName = (g.name || '').toLowerCase().replace(/[_-]/g, '');
          const normalizedDisplayName = (g.displayName || '').toLowerCase().replace(/[_-]/g, '');
          return normalizedName === normalizedSource || normalizedDisplayName === normalizedSource;
        });
      }
      
      if (matchingGuideline) {
        setSelectedDRVSource(matchingGuideline.id);
      }
    }
  }, [client, drvGuidelines]);

  const handleSaveDRVSource = async () => {
    if (!client || !selectedDRVSource) return;
    
    setIsSavingDRVSource(true);
    try {
      await updateClient(client.id, {
        drvFormulaId: selectedDRVSource
      });
      
      // Refresh client data if callback provided
      if (refreshClientData) {
        await refreshClientData();
      }
      
      setIsEditingDRVSource(false);
      showSuccessToast('DRV source updated successfully', 'The dietary reference values will be recalculated based on the new source.');
    } catch (error: any) {
      console.error('Error updating DRV source:', error);
      showErrorToast(error?.message || 'Failed to update DRV source', 'Update Failed');
    } finally {
      setIsSavingDRVSource(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditingDRVSource(false);
    // Reset to original value by finding the matching guideline
    const currentSource = client?.drvSource || (client as any)?.guidelineUsed;
    if (currentSource && drvGuidelines.length > 0) {
      let matchingGuideline = drvGuidelines.find(g => g.id === currentSource);
      if (!matchingGuideline) {
        const normalizedSource = currentSource.toLowerCase().replace(/[_-]/g, '');
        matchingGuideline = drvGuidelines.find(g => {
          const normalizedName = (g.name || '').toLowerCase().replace(/[_-]/g, '');
          const normalizedDisplayName = (g.displayName || '').toLowerCase().replace(/[_-]/g, '');
          return normalizedName === normalizedSource || normalizedDisplayName === normalizedSource;
        });
      }
      if (matchingGuideline) {
        setSelectedDRVSource(matchingGuideline.id);
      }
    }
  };

  // Check if save should be disabled (no changes made)
  const isSaveDisabled = () => {
    const currentSource = client?.drvSource || (client as any)?.guidelineUsed;
    if (!currentSource) return false;
    // Find the guideline that matches current source
    const matchingGuideline = drvGuidelines.find(g => {
      if (g.id === currentSource) return true;
      const normalizedSource = currentSource.toLowerCase().replace(/[_-]/g, '');
      const normalizedName = (g.name || '').toLowerCase().replace(/[_-]/g, '');
      const normalizedDisplayName = (g.displayName || '').toLowerCase().replace(/[_-]/g, '');
      return normalizedName === normalizedSource || normalizedDisplayName === normalizedSource;
    });
    return matchingGuideline?.id === selectedDRVSource;
  };

  return (
    <Card className="lg:col-span-2 shadow-lg hover:shadow-xl transition-all duration-300 border-0 bg-gradient-to-br from-background to-green/5">
      <CardHeader className='pb-0'>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center text-xl font-bold text-foreground">
            Nutrient Guidelines
          </CardTitle>
          {drvRequirements && !isEditingDRVSource && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsEditingDRVSource(true)}
              className="gap-1"
            >
              <Edit2 className="h-4 w-4" />
              Edit
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-4">
        <div className="space-y-4">
          {/* DRV Information */}
          {drvRequirements ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <Label className="text-xs font-semibold text-gray-600">DRV Formula</Label>
                  {isEditingDRVSource ? (
                    <div className="mt-2">
                      <LabeledSelect
                        label=""
                        options={drvGuidelines.map((guideline) => ({
                          label: guideline.displayName || guideline.name || 'Unknown Guideline',
                          value: guideline.id
                        }))}
                        value={selectedDRVSource}
                        onChange={(value) => setSelectedDRVSource(value)}
                        placeholder="Select DRV source"
                        disabled={isLoadingDRVGuidelines || isSavingDRVSource}
                        wrapperClassName="mt-0"
                      />
                    </div>
                  ) : (
                    <div className="text-base font-medium text-gray-900 capitalize">
                      {drvRequirements.source?.replace(/_/g, ' ').toUpperCase() || 'N/A'}
                    </div>
                  )}
                </div>
              </div>
              {isEditingDRVSource && (
                <div className="flex items-center gap-2 pt-2 border-t border-gray-200">
                  <Button
                    size="sm"
                    onClick={handleSaveDRVSource}
                    disabled={isSavingDRVSource || !selectedDRVSource || isSaveDisabled()}
                    className="gap-1"
                  >
                    <Save className="h-4 w-4" />
                    {isSavingDRVSource ? 'Saving...' : 'Save'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleCancelEdit}
                    disabled={isSavingDRVSource}
                    className="gap-1"
                  >
                    <X className="h-4 w-4" />
                    Cancel
                  </Button>
                </div>
              )}
            </>
          ) : (
            <div className="space-y-2">
              <p className="text-sm font-medium text-foreground">Current Guideline</p>
              <p className="text-sm text-muted-foreground">
                {client.guidelineUsed || 'Not specified'}
              </p>
            </div>
          )}
          <p className="text-xs text-gray-500 pt-0">
            This DRV formula determines the recommended daily intake values for macros and micronutrients based on the client's profile and health status.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};
